import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  SwapVert as SortIcon,
  CallMerge as DeduplicateIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { auteursAPI } from '../services/api';

const getInitials = (nom, prenom) => {
  const prenomInitial = prenom ? prenom.charAt(0).toUpperCase() : '';
  const nomInitial = nom ? nom.charAt(0).toUpperCase() : '';
  return `${prenomInitial}${nomInitial}`;
};

const formatNomComplet = (nom, prenom) => {
  return `${prenom || ''} ${nom}`.trim();
};

function AuteursPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sortType, setSortType] = useState('nom-asc'); // 'nom-asc', 'nom-desc', 'livres-desc', 'livres-asc'
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, auteur: null });

  // Requête pour récupérer les auteurs
  const { data, isLoading, error } = useQuery(
    ['auteurs', page, search, sortType],
    () => auteursAPI.getAll({ page, limit: 12, search, sort: sortType }).then(res => res.data),
    {
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000,
    }
  );

  // Mutation pour supprimer un auteur
  const deleteAuteurMutation = useMutation(
    (id) => auteursAPI.delete(id),
    {
      onSuccess: () => {
        toast.success(t('authorsPage.deleteSuccess'));
        queryClient.invalidateQueries('auteurs');
        setDeleteDialog({ open: false, auteur: null });
      },
      onError: (error) => {
        if (error.response?.status === 409) {
          toast.error(t('authorsPage.deleteErrorWithBooks'));
        } else {
          toast.error(t('authorsPage.deleteError'));
        }
      }
    }
  );

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleSortMenuOpen = (event) => {
    setSortMenuAnchor(event.currentTarget);
  };

  const handleSortMenuClose = () => {
    setSortMenuAnchor(null);
  };

  const handleSortSelect = (sortValue) => {
    setSortType(sortValue);
    handleSortMenuClose();
  };

  const getSortLabel = () => {
    switch (sortType) {
      case 'nom-asc':
        return t('authorsPage.sortNameAsc');
      case 'nom-desc':
        return t('authorsPage.sortNameDesc');
      case 'livres-desc':
        return t('authorsPage.sortBooksDesc');
      case 'livres-asc':
        return t('authorsPage.sortBooksAsc');
      default:
        return t('authorsPage.sortNameAsc');
    }
  };

  const handleDeduplicate = () => {
    navigate('/auteurs/doublons');
  };

  const handleAdd = () => {
    navigate('/auteurs/ajouter');
  };

  const handleDelete = (auteur) => {
    setDeleteDialog({ open: true, auteur });
  };

  const confirmDelete = () => {
    if (deleteDialog.auteur) {
      deleteAuteurMutation.mutate(deleteDialog.auteur.id);
    }
  };


  if (error) {
    return (
      <Alert severity="error">
        {t('authorsPage.loadingError', { message: error.message })}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Barre de recherche et boutons */}
      <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, mb: 3, alignItems: 'center', flexWrap: { xs: 'nowrap', sm: 'nowrap' } }}>
        <TextField
          fullWidth
          placeholder={t('authorsPage.searchPlaceholder')}
          value={search}
          onChange={handleSearchChange}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.default',
              borderRadius: 2,
            }
          }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
          }}
        />
        <Button
          variant="outlined"
          startIcon={<SortIcon />}
          onClick={handleSortMenuOpen}
          sx={{
            height: '52px',
            borderRadius: 2,
            fontSize: { xs: '14px', sm: '14px' },
            whiteSpace: 'nowrap',
            backgroundColor: 'background.default',
            px: { xs: 1, sm: 1.5 },
            flexShrink: 0,
            '& .MuiButton-startIcon': {
              mr: { xs: 0, sm: 0.75 }
            }
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {getSortLabel()}
          </Box>
        </Button>
        <Menu
          anchorEl={sortMenuAnchor}
          open={Boolean(sortMenuAnchor)}
          onClose={handleSortMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem
            onClick={() => handleSortSelect('nom-asc')}
            selected={sortType === 'nom-asc'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {sortType === 'nom-asc' && (
                <CheckIcon sx={{ mr: 1, fontSize: '18px' }} />
              )}
              {sortType !== 'nom-asc' && <Box sx={{ width: '26px' }} />}
              {t('authorsPage.sortNameAsc')}
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleSortSelect('nom-desc')}
            selected={sortType === 'nom-desc'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {sortType === 'nom-desc' && (
                <CheckIcon sx={{ mr: 1, fontSize: '18px' }} />
              )}
              {sortType !== 'nom-desc' && <Box sx={{ width: '26px' }} />}
              {t('authorsPage.sortNameDesc')}
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleSortSelect('livres-desc')}
            selected={sortType === 'livres-desc'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {sortType === 'livres-desc' && (
                <CheckIcon sx={{ mr: 1, fontSize: '18px' }} />
              )}
              {sortType !== 'livres-desc' && <Box sx={{ width: '26px' }} />}
              {t('authorsPage.sortBooksDesc')}
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleSortSelect('livres-asc')}
            selected={sortType === 'livres-asc'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {sortType === 'livres-asc' && (
                <CheckIcon sx={{ mr: 1, fontSize: '18px' }} />
              )}
              {sortType !== 'livres-asc' && <Box sx={{ width: '26px' }} />}
              {t('authorsPage.sortBooksAsc')}
            </Box>
          </MenuItem>
        </Menu>
        <Button
          variant="outlined"
          startIcon={<DeduplicateIcon />}
          onClick={handleDeduplicate}
          sx={{
            height: '52px',
            borderRadius: 2,
            fontSize: { xs: '14px', sm: '14px' },
            whiteSpace: 'nowrap',
            backgroundColor: 'background.default',
            px: { xs: 1, sm: 1.5 },
            flexShrink: 0,
            '& .MuiButton-startIcon': {
              mr: { xs: 0, sm: 0.75 }
            }
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {t('authorsPage.duplicates')}
          </Box>
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{
            height: '52px',
            borderRadius: 2,
            fontSize: { xs: '14px', sm: '14px' },
            whiteSpace: 'nowrap',
            px: { xs: 1, sm: 1.5 },
            flexShrink: 0,
            '& .MuiButton-startIcon': {
              mr: { xs: 0, sm: 0.75 }
            }
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {t('authorsPage.add')}
          </Box>
        </Button>
      </Box>

      {/* Résultats */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {data?.data?.length === 0 ? (
            <Alert severity="info">
              {search ? t('authorsPage.noAuthorsFound') : t('authorsPage.noAuthors')}
            </Alert>
          ) : (
            <Card sx={{ borderRadius: 2 }}>
              <Box sx={{ p: 3, pb: 2 }}>
                <Typography 
                  variant="h6" 
                  component="h2"
                  sx={{ 
                    fontWeight: 700,
                    fontSize: '18px',
                    mb: 0.5,
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                  }}
                >
                  {t('authorsPage.authorsList')}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                  }}
                >
                  {data?.pagination?.total || 0} {data?.pagination?.total === 1 ? t('authorsPage.author') : t('authorsPage.authors')}
                </Typography>
              </Box>
              <Divider />
              <List sx={{ py: 0 }}>
                {data?.data?.map((auteur, index) => (
                  <React.Fragment key={auteur.id}>
                    <ListItem
                      button
                      onClick={() => navigate(`/auteurs/${auteur.id}`)}
                      sx={{
                        py: 2,
                        px: 3,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {getInitials(auteur.nom, auteur.prenom)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 700,
                              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                            }}
                          >
                            {formatNomComplet(auteur.nom, auteur.prenom)}
                          </Typography>
                        }
                        secondary={
                          auteur.nationalite && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                              }}
                            >
                              {auteur.nationalite}
                            </Typography>
                          )
                        }
                      />
                      <Box sx={{ textAlign: 'right', ml: 2 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 400,
                            fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                          }}
                        >
                          {auteur.nombre_livres || 0}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                          }}
                        >
                          {auteur.nombre_livres === 1 ? t('authorsPage.book') : t('authorsPage.books')}
                        </Typography>
                      </Box>
                    </ListItem>
                    {index < data.data.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>

              {/* Pagination */}
              {data?.pagination?.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, pt: 2 }}>
                  <Pagination
                    count={data.pagination.totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                  />
                </Box>
              )}
            </Card>
          )}
        </>
      )}

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, auteur: null })}
      >
        <DialogTitle>{t('authorsPage.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('authorsPage.deleteConfirm', { name: deleteDialog.auteur?.nom })}
          </Typography>
          {deleteDialog.auteur?.nombre_livres > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('authorsPage.deleteWarning', { count: deleteDialog.auteur.nombre_livres })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, auteur: null })}>
            {t('authorsPage.cancel')}
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            disabled={deleteAuteurMutation.isLoading || deleteDialog.auteur?.nombre_livres > 0}
          >
            {deleteAuteurMutation.isLoading ? t('authorsPage.deleting') : t('authorsPage.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AuteursPage;
